import * as React from "react";
import { cn } from "../../lib/utils.js";

/**
 * Card primitive with composable sub-components.
 *
 * <Card>
 *   <Card.Header>…</Card.Header>
 *   <Card.Body>…</Card.Body>
 *   <Card.Footer>…</Card.Footer>
 * </Card>
 *
 * @param {"panel"|"entity"} [variant="panel"]
 *   - `panel`  — la caja de siempre: contenedor de un bloque de contenido.
 *   - `entity` — la tarjeta que REPRESENTA UNA FILA (una unidad, una sesión,
 *     un vehículo). Misma anatomía que las tarjetas en que colapsan las tablas
 *     en móvil: fondo elevado, banda de cabecera con la identidad y cuerpo
 *     debajo. Sin relleno propio —lo reparte cada banda— y con `overflow-hidden`
 *     para que la banda respete la esquina redondeada.
 */
export function Card({ className, variant = "panel", children, ...props }) {
  return (
    <div
      className={cn(
        // `sheen-1` es la cara del material: un degradado transparente que
        // recoge la luz cenital arriba y se apaga abajo. Se compone sobre el
        // `bg-*` de la tarjeta, no lo reemplaza. Junto con el canto que ya
        // trae `shadow-card`, es lo que hace que la tarjeta se lea como un
        // objeto apoyado y no como un rectángulo de color.
        "rounded-xl border shadow-card sheen-1 text-text-primary",
        variant === "entity"
          ? "overflow-hidden border-border-muted bg-background-elevated"
          : "border-border bg-surface-1",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * @param {boolean} [band=false] — fondo propio, un paso más claro que la
 *   tarjeta. Es lo que separa la identidad del contenido de un vistazo en las
 *   tarjetas de entidad; en un panel de contenido normalmente sobra.
 */
function CardHeader({ className, band = false, children, ...props }) {
  return (
    <div
      className={cn(
        // Sin cuerpo debajo no hay nada que separar: la divisoria quedaría
        // pegada al borde de la tarjeta, leyéndose como un borde doble.
        "px-4 py-3 border-b border-border-muted [&:last-child]:border-b-0",
        "flex items-center justify-between gap-2",
        band && "bg-background-card sheen-chrome",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
CardHeader.displayName = "Card.Header";

function CardBody({ className, children, ...props }) {
  return (
    <div className={cn("px-4 py-3", className)} {...props}>
      {children}
    </div>
  );
}
CardBody.displayName = "Card.Body";

function CardFooter({ className, children, ...props }) {
  return (
    <div
      className={cn(
        "px-4 py-3 border-t border-border-muted",
        "flex items-center justify-end gap-2",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
CardFooter.displayName = "Card.Footer";

Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;
